import { EventRoleDTO } from "@/types/EventRoleDTO";
import { EventRole } from "@/types/EventRole";
import { apiDelete, apiPut, apiGet, apiPost } from "../_apiShim";
import { EventRoleCreationDTO } from "@/types/EventRoleCreationDTO";
import { TransferInvitationStatus } from "@/types/TransferInvitation";

const BASE_PATH = "/api/v1/event-roles/";

// Get event role by user ID
export async function getEventRoleByUserId(
  eventId: string,
  fields?: string,
): Promise<EventRoleDTO> {
  const queryParams = fields ? `?fields=${encodeURIComponent(fields)}` : "";

  return await apiGet<EventRoleDTO>(
    `${BASE_PATH}${eventId}${queryParams}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

// Get paginated event roles
export async function getPaginatedEventRoles(
  eventId: string,
  page: number,
  pageSize: number,
): Promise<EventRoleDTO[]> {
  const queryParams = `?page=${encodeURIComponent(
    page,
  )}&pageSize=${encodeURIComponent(pageSize)}`;

  return await apiGet<EventRoleDTO[]>(
    `${BASE_PATH}${eventId}/paginatedEventRoles${queryParams}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

// Get invalid event roles for user
export async function getInvalidEventRolesUser(
  fields: string,
  page: number = 1,
  pageSize: number = 10,
): Promise<EventRole[]> {
  const queryParams = `?fields=${encodeURIComponent(
    fields,
  )}&page=${encodeURIComponent(page)}&pageSize=${encodeURIComponent(pageSize)}`;

  return await apiGet<EventRole[]>(
    `${BASE_PATH}invalid-for-user${queryParams}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

export async function deleteEventRole(
  eventId: string,
  email?: string,
): Promise<void> {
  // Build query parameters for the backend, filtering out undefined values
  const queryParams = new URLSearchParams();

  if (email) {
    queryParams.append("email", email);
  }

  const queryString = queryParams.toString();
  const url = queryString
    ? `${BASE_PATH}${eventId}?${queryString}`
    : `${BASE_PATH}${eventId}`;

  await apiDelete<void>(url, {
    "Cache-Control": "no-store",
  });
}

// Accept an event role
export async function acceptEventRole(
  eventId: string,
): Promise<void> {
  await apiPut<void>(
    `${BASE_PATH}${eventId}/accept-role`,
    {},
    {
      "Cache-Control": "no-store",
    },
  );
}

// Update an event role
export async function updateEventRole(
  eventRole: EventRole,
): Promise<void> {
  await apiPut<void>(
    `${BASE_PATH}${eventRole.eventId}/update-role`,
    {
      eventRoleId: eventRole.eventRoleId,
      role: eventRole.role,
      // Forward only when caller actually sets it — BE rejects templateId on
      // non-CUSTOM targets and the partial $set semantics rely on undefined
      // == "no change requested".
      ...(eventRole.templateId ? { templateId: eventRole.templateId } : {}),
      // Same partial semantics: undefined = "not requested" (BE preserves the
      // stored value), [] = "clear it". Never send it as null.
      ...(eventRole.surfaceDefaults !== undefined
        ? { surfaceDefaults: eventRole.surfaceDefaults }
        : {}),
    },
    {
      "Cache-Control": "no-store",
    },
  );
}

// Initiate an event change. `keepInitiatorAsAdmin` is sent only when the
// caller passes a boolean (offboarding strip flag on); otherwise the body is
// exactly `{newOwnerId}`, which the backend reads as "keep" (Q4).
export async function initiateEventOwnerChange(
  eventId: string,
  newOwnerId: string,
  keepInitiatorAsAdmin?: boolean,
): Promise<void> {
  await apiPost<void>(
    `${BASE_PATH}${eventId}/initiate-event-change`,
    typeof keepInitiatorAsAdmin === "boolean"
      ? { newOwnerId, keepInitiatorAsAdmin }
      : { newOwnerId },
    {
      "Cache-Control": "no-store",
    },
  );
}

// Same-company instant owner hand-off (event stays in its company)
export async function handoffEventOwner(
  eventId: string,
  newOwnerId: string,
): Promise<void> {
  await apiPost<void>(
    `${BASE_PATH}${eventId}/handoff-owner`,
    { newOwnerId },
    {
      "Cache-Control": "no-store",
    },
  );
}

// Pending cross-company transfer status (always 200; pending:false when none)
export async function getTransferInvitation(
  eventId: string,
): Promise<TransferInvitationStatus> {
  return await apiGet<TransferInvitationStatus>(
    `${BASE_PATH}${eventId}/transfer-invitation`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

// Cancel (retract) a pending cross-company transfer
export async function cancelTransferInvitation(
  eventId: string,
): Promise<void> {
  await apiDelete<void>(`${BASE_PATH}${eventId}/transfer-invitation`, {
    "Cache-Control": "no-store",
  });
}

// Decline an event role
export async function declineEventRole(
  eventId: string,
  type: string,
): Promise<void> {
  await apiPut<void>(
    `${BASE_PATH}${eventId}/decline-role`,
    {
      type,
    },
    {
      "Cache-Control": "no-store",
    },
  );
}

export type InviteSkipReason =
  | "ALREADY_ON_EVENT"
  | "GUEST_OPTED_OUT"
  | "NO_COMPANY_ROLE"
  | "USER_NOT_INVITABLE"
  // Members wave A1–A3: the batch doors judge every address.
  | "INVALID_EMAIL"
  | "DUPLICATE_IN_REQUEST"
  | "ALREADY_MEMBER"
  | "ALREADY_INVITED"
  | "COLLABORATOR_LIMIT_REACHED"
  | "INVITE_FAILED";

export type InviteOutcome = {
  // Legacy alias kept for one release so older clients can still parse a
  // newer BE response. Equal to `requested`. Drop alongside BE in PR#2.
  count: number;
  requested: number;
  inserted: number;
  skipped: { email: string; reason: InviteSkipReason }[];
};

export async function createCollaboratorEventRoles(
  eventId: string,
  eventRoles: EventRoleCreationDTO[],
): Promise<InviteOutcome> {
  // Authenticated cockpit endpoint — returns granular skip reasons for
  // same-org rows (ALREADY_ON_EVENT / GUEST_OPTED_OUT / NO_COMPANY_ROLE)
  // and a collapsed USER_NOT_INVITABLE for cross-org rows so an
  // operator can't enumerate opt-out vs no-role for emails outside
  // their company. Since members wave A3 an address without an account is
  // one more USER_NOT_INVITABLE skip (it used to fail the whole batch), and
  // invalid / repeated addresses come back as INVALID_EMAIL /
  // DUPLICATE_IN_REQUEST — every skip in request order, as sent.
  return await apiPost<InviteOutcome>(
    `${BASE_PATH}${eventId}/collaborators`,
    eventRoles,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}
